/**
 * Utility for resolving local image URLs on mobile platforms (iOS, Android).
 * Mobile platforms return image URLs that lack protocols (e.g., bare filenames)
 * which need to be resolved to full localhost storage URLs for fetching.
 */

const MOBILE_STORAGE_BASE_URL = 'https://localhost:4000/storage?file=';
const VALID_PROTOCOLS = ['http://', 'https://', 'blob:', 'data:'];

/**
 * Check if a URL needs to be resolved via local storage server.
 */
export const needsLocalServerResolution = (url: string): boolean => {
  if (!url) return false;
  return VALID_PROTOCOLS.every((protocol) => !url.startsWith(protocol));
};

/**
 * Check if URL is a localhost/local server URL that external APIs cannot access.
 */
export const isLocalUrl = (url: string): boolean => {
  if (needsLocalServerResolution(url)) {
    return true;
  }

  try {
    const urlObj = new URL(url);
    if (urlObj.protocol === 'blob:' || urlObj.protocol === 'data:') {
      return true;
    }

    return (
      urlObj.hostname === 'localhost' ||
      urlObj.hostname === '127.0.0.1' ||
      urlObj.hostname === '0.0.0.0' ||
      urlObj.hostname.endsWith('.local')
    );
  } catch {
    return false;
  }
};

/**
 * Convert a mobile URL (lacking protocol) to a full localhost storage URL.
 */
export const resolveLocalUrl = (url: string): string => {
  if (needsLocalServerResolution(url)) {
    return `${MOBILE_STORAGE_BASE_URL}${encodeURIComponent(url)}`;
  }
  return url;
};
